import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import Modal from "react-bootstrap/Modal";
import Checkbox from "@mui/material/Checkbox";
import { RenderIf, Button, SearchInput, UserCard } from "shared/components";
import { useSendNewsMutation } from "redux/api/news";
import { useGetAllUsersQuery } from "redux/api/siteUsers";
import Member from "shared/media/user/userIcon.png";
import { initialValues } from "./data";

const { REACT_APP_BASE_PHOTO_URL } = process.env;

const label = { inputProps: { "aria-label": "Checkbox demo" } };

const SendModal = ({ id, setModal, modal }) => {
  const { t } = useTranslation();

  const { data: siteUsers } = useGetAllUsersQuery({});
  const [sendNews, { isSuccess, isLoading, error }] = useSendNewsMutation();

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      const data = {
        newsId: id,
        ids: values.selectedIds,
        emails: values.selectedEmails,
      };

      values.selectedIds.length && sendNews(data);
    },
  });

  const filteredUsers = React.useMemo(() => {
    if (!formik.values.search) return [];

    return siteUsers?.data?.filter((u) =>
      `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(formik.values.search.toLowerCase())
    );
  }, [formik.values.search, siteUsers]);

  React.useEffect(() => {
    if (isSuccess) {
      toast.success("Send successfully !", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });

      formik.resetForm();
      setModal(false);
    }
  }, [isSuccess]);

  React.useEffect(() => {
    if (error) {
      toast.error("Please try again !", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [error]);

  const handleSelect = (email, id, checked) => {
    if (checked) {
      formik.setFieldValue("selectedIds", [...formik.values.selectedIds, id]);
      formik.setFieldValue("selectedEmails", [
        ...formik.values.selectedEmails,
        email,
      ]);
    } else {
      formik.setFieldValue(
        "selectedIds",
        formik.values.selectedIds.filter((i) => i !== id)
      );
      formik.setFieldValue(
        "selectedEmails",
        formik.values.selectedEmails.filter((e) => e !== email)
      );
    }
  };

  return (
    <Modal
      size="lg"
      centered
      keyboard={false}
      backdrop="static"
      show={modal}
      onHide={() => setModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{t("sendPost")}</Modal.Title>
      </Modal.Header>

      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className="sendList">
            <SearchInput
              name="search"
              placeholder={t("search")}
              filterInputs={formik.values.search}
              filterInputsUpdate={(e) =>
                formik.setFieldValue("search", e.target.value)
              }
            />

            <div className="sendListBody">
              {filteredUsers?.map((user) => (
                <div key={user.id} className="sendListBodyItem d-flex">
                  <div className="sendListBodyItemLeft">
                    <UserCard
                      image={
                        user.profilePhotoUrl
                          ? `${REACT_APP_BASE_PHOTO_URL}${user.profilePhotoUrl}`
                          : Member
                      }
                      userName={user.firstName}
                      userLastName={user.lastName}
                      profession={user.position}
                    />
                  </div>

                  <div className="sendListBodyItemRight">
                    <Checkbox
                      {...label}
                      color="success"
                      checked={formik.values.selectedEmails.includes(
                        user.email
                      )}
                      onChange={(e) =>
                        handleSelect(user.email, user.id, e.target.checked)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="pt_14_26 font_14 align-items-center d-flex"
            background="#057953"
            type="submit"
            disabled={!formik.values.selectedIds.length || isLoading}>
            {t("share")}

            <RenderIf condition={isLoading}>
              <div className="spinner-border" role="status"></div>
            </RenderIf>
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default SendModal;
